import { ApiResponse } from '@interfaces/index';
import { FreedomToSpeakUpConcernListItem } from '@shared/types/freedomToSpeakUpConcern';
import { apiUrlConfig } from './configs/apiUrlConfig';
import createApiClient from './configs/httpHandler';

/** Matches hapstar-be-action-plan `FreedomToSpeakUp_v2` routes (v2). */
export const FREEDOM_TO_SPEAK_UP_API_ROUTES = {
  formOptions: '/freedom-to-speak-up/form-options',
  createConcern: '/freedom-to-speak-up',
  listConcerns: '/freedom-to-speak-up',
  concernById: (concernId: number) => `/freedom-to-speak-up/${concernId}`,
  responses: (concernId: number) => `/freedom-to-speak-up/${concernId}/responses`,
  responsesSeen: (concernId: number) => `/freedom-to-speak-up/${concernId}/responses/seen`,
  companyConcerns: '/freedom-to-speak-up/company-concerns',
  companyConcernById: (concernId: number) => `/freedom-to-speak-up/company-concerns/${concernId}`,
  companyConcernStatus: (concernId: number) =>
    `/freedom-to-speak-up/company-concerns/${concernId}/status`,
  companyConcernResponses: (concernId: number) =>
    `/freedom-to-speak-up/company-concerns/${concernId}/responses`,
  companyConcernResponsesSeen: (concernId: number) =>
    `/freedom-to-speak-up/company-concerns/${concernId}/responses/seen`,
} as const;

export interface FreedomToSpeakUpFormOption {
  id: number;
  label: string;
  sortOrder: number;
}

export interface FreedomToSpeakUpFormOptionsData {
  relateOptions: FreedomToSpeakUpFormOption[];
  categoryOptions: FreedomToSpeakUpFormOption[];
}

export interface CreateFreedomToSpeakUpConcernPayload {
  message: string;
  shareMyIdentity: boolean;
  urgent: boolean;
  concernRelatesToOptionId: number;
  categoryOptionIds: number[];
}

export interface CreateFreedomToSpeakUpConcernData {
  id: number;
  referenceNumber: string;
  createdAt: string;
  statusOptionId: number;
  statusKey: string;
  statusLabel: string;
}

/** Raw list item from BE (camelCase); map to card type below. */
export interface FreedomToSpeakUpConcernApiListItem {
  id: number;
  referenceNumber: string;
  createdAt: string;
  subject: string;
  status: string;
  statusLabel: string;
  statusKey?: string;
  responseDate: string | null;
  responseHeading: string;
  response: string;
  urgent?: boolean;
  hasUnreadReply?: boolean;
}

export interface FreedomToSpeakUpConcernsListData {
  items: FreedomToSpeakUpConcernApiListItem[];
}

export interface FreedomToSpeakUpConcernDetail {
  id: number;
  referenceNumber: string;
  message: string;
  shareMyIdentity: boolean;
  urgent: boolean;
  createdAt: string;
  relatesTo: { id: number; label: string } | null;
  categories: { id: number; label: string }[];
  status: { id: number; key: string; label: string } | null;
  responded: boolean;
}

export interface FreedomToSpeakUpThreadItem {
  id: number;
  authorDisplay: string;
  message: string;
  createdAt: string;
  isFromCurrentUser: boolean;
  seen: boolean;
  respondedAs?: string;
}

export interface FreedomToSpeakUpResponsesData {
  items: FreedomToSpeakUpThreadItem[];
}

export interface MarkFreedomToSpeakUpResponsesSeenPayload {
  updateAs: 'user';
}

export interface MarkFreedomToSpeakUpResponsesSeenData {
  updatedCount: number;
}

/** Employer admin portal — paginated list (hapstar-be-action-plan). */
export interface EmployerFreedomConcernPagination {
  total: number;
  lastPage: number;
  prevPage: number | null;
  nextPage: number | null;
  perPage: number;
  currentPage: number;
  from: number;
  to: number;
}

export interface EmployerFreedomConcernApiListItem {
  id: number;
  referenceNumber: string;
  createdAt: string;
  adminStatus: string;
  statusLabel: string;
  urgent: boolean;
  daysOpen: number;
  senderDisplay: string;
  /** When false, employer UI must not show reporter PII from other fields. */
  shareMyIdentity?: boolean;
  messagePreview: string;
  categories: string[];
}

export interface EmployerFreedomCompanyConcernsListData {
  data: EmployerFreedomConcernApiListItem[];
  pagination: EmployerFreedomConcernPagination;
}

export interface EmployerFreedomConcernApiReporter {
  userId: number;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
}

export interface EmployerFreedomConcernApiTimelineEvent {
  statusKey: string;
  statusLabel: string;
  createdAt: string;
  updatedByDisplay: string;
}

export interface EmployerFreedomConcernApiMessage {
  id: number;
  authorDisplay: string;
  message: string;
  createdAt: string;
  isFromEmployee: boolean;
  isFromCurrentUser: boolean;
  seen: boolean;
  shareMyIdentity?: boolean;
  respondedAs?: string;
}

export interface EmployerFreedomConcernApiDetail {
  id: number;
  referenceNumber: string;
  createdAt: string;
  submittedAt: string;
  adminStatus: string;
  statusLabel: string;
  statusOptionId: number;
  urgent: boolean;
  daysOpen: number;
  senderDisplay: string;
  shareMyIdentity: boolean;
  messagePreview: string;
  description: string;
  categories: string[];
  relatesTo: { id: number; label: string } | null;
  assignedTo: { id: number; displayName: string } | null;
  responded: boolean;
  archived: boolean;
  reporter: EmployerFreedomConcernApiReporter | null;
  timeline: EmployerFreedomConcernApiTimelineEvent[];
  messages: EmployerFreedomConcernApiMessage[];
}

export interface UpdateEmployerFreedomConcernStatusPayload {
  statusKey?: string;
  statusOptionId?: number;
  assignedToUserId?: number | null;
}

export interface UpdateEmployerFreedomConcernStatusData {
  id: number;
  statusOptionId: number;
  statusKey: string;
  statusLabel: string;
  assignedToUserId: number | null;
}

export interface EmployerFreedomConcernResponsesData {
  items: EmployerFreedomConcernApiMessage[];
}

export interface MarkEmployerFreedomResponsesSeenPayload {
  updateAs: 'admin';
}

const VALID_CARD_STATUSES = new Set<FreedomToSpeakUpConcernListItem['status']>([
  'new',
  'reply',
  'in_progress',
  'awaiting',
  'resolved',
]);

function mapListItemToCard(
  item: FreedomToSpeakUpConcernApiListItem,
): FreedomToSpeakUpConcernListItem {
  let status = item.status as FreedomToSpeakUpConcernListItem['status'];

  if (!VALID_CARD_STATUSES.has(status)) {
    if (item.hasUnreadReply || item.statusKey === 'reply') {
      status = 'reply';
    } else if (item.statusKey === 'in_progress') {
      status = 'in_progress';
    } else if (item.statusKey === 'resolved' || item.statusKey === 'closed') {
      status = 'resolved';
    } else if (item.statusKey === 'new') {
      status = 'new';
    } else {
      status = 'awaiting';
    }
  }

  return {
    id: item.id,
    referenceNumber: item.referenceNumber,
    createdAt: item.createdAt,
    subject: item.subject,
    status,
    statusLabel: item.statusLabel,
    responseDate: item.responseDate,
    responseHeading: item.responseHeading,
    response: item.response,
    hasUnreadReply: Boolean(item.hasUnreadReply),
  };
}

export function mapFreedomToSpeakUpConcernsList(
  data: FreedomToSpeakUpConcernsListData | FreedomToSpeakUpConcernApiListItem[] | undefined,
): FreedomToSpeakUpConcernListItem[] {
  if (!data) {
    return [];
  }

  const items = Array.isArray(data) ? data : data.items;

  return items.map(mapListItemToCard);
}

export const freedomToSpeakUpApiHandler = {
  getFormOptions: async (): Promise<ApiResponse<FreedomToSpeakUpFormOptionsData>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(
      FREEDOM_TO_SPEAK_UP_API_ROUTES.formOptions,
    ),

  createConcern: async (
    payload: CreateFreedomToSpeakUpConcernPayload,
  ): Promise<ApiResponse<CreateFreedomToSpeakUpConcernData>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).post(
      FREEDOM_TO_SPEAK_UP_API_ROUTES.createConcern,
      payload,
    ),

  getMyConcerns: async (): Promise<ApiResponse<FreedomToSpeakUpConcernsListData>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(
      FREEDOM_TO_SPEAK_UP_API_ROUTES.listConcerns,
    ),

  getConcernById: async (concernId: number): Promise<ApiResponse<FreedomToSpeakUpConcernDetail>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(
      FREEDOM_TO_SPEAK_UP_API_ROUTES.concernById(concernId),
    ),

  getResponses: async (concernId: number): Promise<ApiResponse<FreedomToSpeakUpResponsesData>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(
      FREEDOM_TO_SPEAK_UP_API_ROUTES.responses(concernId),
    ),

  createResponse: async (
    concernId: number,
    message: string,
  ): Promise<ApiResponse<FreedomToSpeakUpThreadItem>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).post(
      FREEDOM_TO_SPEAK_UP_API_ROUTES.responses(concernId),
      { message },
    ),

  markResponsesSeen: async (
    concernId: number,
    payload: MarkFreedomToSpeakUpResponsesSeenPayload,
  ): Promise<ApiResponse<MarkFreedomToSpeakUpResponsesSeenData>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).put(
      FREEDOM_TO_SPEAK_UP_API_ROUTES.responsesSeen(concernId),
      payload,
    ),

  getCompanyConcerns: async (params: {
    statusKey?: string;
    page?: number;
    pageSize?: number;
    urgentOnly?: boolean;
  }): Promise<ApiResponse<EmployerFreedomCompanyConcernsListData>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(
      FREEDOM_TO_SPEAK_UP_API_ROUTES.companyConcerns,
      { params },
    ),

  getCompanyConcernById: async (
    concernId: number,
  ): Promise<ApiResponse<EmployerFreedomConcernApiDetail>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(
      FREEDOM_TO_SPEAK_UP_API_ROUTES.companyConcernById(concernId),
    ),

  updateCompanyConcernStatus: async (
    concernId: number,
    payload: UpdateEmployerFreedomConcernStatusPayload,
  ): Promise<ApiResponse<UpdateEmployerFreedomConcernStatusData>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).put(
      FREEDOM_TO_SPEAK_UP_API_ROUTES.companyConcernStatus(concernId),
      payload,
    ),

  getCompanyConcernResponses: async (
    concernId: number,
  ): Promise<ApiResponse<EmployerFreedomConcernResponsesData>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(
      FREEDOM_TO_SPEAK_UP_API_ROUTES.companyConcernResponses(concernId),
    ),

  createCompanyConcernResponse: async (
    concernId: number,
    message: string,
  ): Promise<ApiResponse<EmployerFreedomConcernApiMessage>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).post(
      FREEDOM_TO_SPEAK_UP_API_ROUTES.companyConcernResponses(concernId),
      { message },
    ),

  markCompanyConcernResponsesSeen: async (
    concernId: number,
  ): Promise<ApiResponse<MarkFreedomToSpeakUpResponsesSeenData>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).put(
      FREEDOM_TO_SPEAK_UP_API_ROUTES.companyConcernResponsesSeen(concernId),
      { updateAs: 'admin' },
    ),
};
